import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PackagePlus, Clock, Calendar, FileText } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchVerifyNewStockReceivedQueue,
    fetchViewNewStockReceivedGrid,
    approveNewStockIssueRecieved,
    clearNewStockReceivedItems,
    clearNewStockReceivedVerifyResult,
    resetVerifyNewStockReceived,
    selectVNSRQueue, selectVNSRQueueLoading, selectVNSRQueueError,
    selectVNSRItems, selectVNSRItemsLoading,
    selectVNSRVerifyLoading,
} from '../../slices/purchaseSlice/verifyNewStockReceivedSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const num = (v) => parseFloat(v || 0) || 0;

// Legacy's own dropdown-building JS always drops the "Return" status for this page.
const EXCLUDED_ACTIONS = ['return'];

const VerifyNewStockReceived = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    const queue = useSelector(selectVNSRQueue);
    const queueLoading = useSelector(selectVNSRQueueLoading);
    const queueError = useSelector(selectVNSRQueueError);
    const items = useSelector(selectVNSRItems);
    const itemsLoading = useSelector(selectVNSRItemsLoading);
    const verifyLoading = useSelector(selectVNSRVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [checkedRows, setCheckedRows] = useState({});
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    useEffect(() => {
        if (roleId && uid) dispatch(fetchVerifyNewStockReceivedQueue({ roleId, userid: uid }));
    }, [roleId, uid, dispatch]);

    useEffect(() => {
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyNewStockReceived());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem) {
            dispatch(fetchViewNewStockReceivedGrid(selectedItem.Refno));
            dispatch(fetchStatusList({ MOID: selectedItem.MOID, ROID: roleId, ChkAmt: 0 }));
            dispatch(setSelectedMOID(selectedItem.MOID));
            dispatch(fetchRemarks({ trno: selectedItem.Refno, moid: selectedItem.MOID }));
            setCheckedRows({});
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId && uid) dispatch(fetchVerifyNewStockReceivedQueue({ roleId, userid: uid }));
        if (selectedItem) dispatch(fetchViewNewStockReceivedGrid(selectedItem.Refno));
    };

    const handleItemSelect = (item) => setSelectedItem(item);

    const toggleRow = (idx) => setCheckedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
    const toggleAllRows = (checked) => {
        const next = {};
        items.forEach((_, idx) => { next[idx] = checked; });
        setCheckedRows(next);
    };
    const allRowsChecked = items.length > 0 && items.every((_, idx) => checkedRows[idx]);

    const totals = useMemo(() => items.reduce((acc, r) => {
        acc.Quantity += num(r.Quantity);
        acc.Amount += num(r.Amount);
        return acc;
    }, { Quantity: 0, Amount: 0 }), [items]);

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyNewStockReceivedQueue({ roleId, userid: uid }));
            setSelectedItem(null);
            setCheckedRows({});
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearNewStockReceivedItems());
            dispatch(clearNewStockReceivedVerifyResult());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!selectedItem) { toast.error('No transfer selected'); return; }
        if (!comment.trim()) { toast.error('Enter Remarks'); return; }
        if (items.length === 0) { toast.error('Invalid Submission'); return; }
        if (!allRowsChecked) { toast.error('Please Verify Item Codes'); return; }

        const actionValue = action.value || action.type;
        // ActionRemarks/RoleID/Createdby match PurchaseDataLayer.ApproveNewStockIssueRecieved's
        // actual bound property names, not the plain "Remarks" key legacy's own JS payload used.
        const payload = {
            Refno: selectedItem.Refno,
            ActionRemarks: comment.trim(),
            Appstatus: actionValue,
            RoleID: roleId,
            Createdby: getCurrentUser(),
        };

        try {
            const result = await dispatch(approveNewStockIssueRecieved(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status && status !== 'Submitted') { toast.error(status); return; }
            toast.success(`Reference No ${selectedItem.Refno} ${actionValue}d successfully`);
            finishAndRefresh();
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit verification');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.Refno?.toLowerCase().includes(q) || item.Indentno?.toLowerCase().includes(q) || item.FromCC?.toLowerCase().includes(q) || item.ToCC?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-emerald-200 dark:border-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-100 dark:from-emerald-800/50 dark:to-emerald-800/50 flex items-center justify-center">
                    <PackagePlus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.Refno}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.FromCC} → {item.ToCC}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1"><Calendar className="w-3 h-3" /><span>{item.Date}</span></span>
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">{item.Indentno}</span>
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[120px] truncate">{item.Refno}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.FromCC} → {item.ToCC}</span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]"><Calendar className="w-3 h-3" /> {item.Date}</span>
            <span className="ml-auto px-2 py-0.5 text-xs rounded-full border bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 whitespace-nowrap">{item.Indentno}</span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-emerald-200 dark:border-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-100 dark:from-emerald-800/50 dark:to-emerald-800/50 flex items-center justify-center">
            <PackagePlus className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        </div>
    );

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-6 border-2 border-emerald-200 dark:border-emerald-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{selectedItem.Refno}</h2>
                    <p className="text-emerald-600 dark:text-emerald-400 font-semibold mb-3">{selectedItem.FromCC} → {selectedItem.ToCC}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-medium">Indent: {selectedItem.Indentno}</span>
                        <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium">Received: {selectedItem.Date}</span>
                        {selectedItem.Expdate && <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Expected: {selectedItem.Expdate}</span>}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                            <div className="p-1.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg"><FileText className="w-4 h-4 text-white" /></div>
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Item Details</h3>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input type="checkbox" checked={allRowsChecked} onChange={(e) => toggleAllRows(e.target.checked)} />
                            Select All
                        </label>
                    </div>

                    {itemsLoading ? (
                        <div className="flex items-center justify-center gap-3 py-8"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div><span className="text-sm text-gray-500">Loading items…</span></div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">No item details found.</p>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                <thead className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                                    <tr>
                                        <th className="px-2 py-1.5 text-center font-semibold text-gray-700 dark:text-gray-300">Chk</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Item Code</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Item Name</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Specification</th>
                                        <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Quantity</th>
                                        <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {items.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-emerald-50 dark:hover:bg-emerald-900/10">
                                            <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!checkedRows[idx]} onChange={() => toggleRow(idx)} /></td>
                                            <td className="px-2 py-1 whitespace-nowrap font-medium">{row.Itemcode}</td>
                                            <td className="px-2 py-1">{row.Itemname}</td>
                                            <td className="px-2 py-1">{row.Specification}</td>
                                            <td className="px-2 py-1 text-right">{num(row.Quantity).toFixed(4)}</td>
                                            <td className="px-2 py-1 text-right">{num(row.Amount).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                        <td className="px-2 py-1" colSpan={4}>TOTAL</td>
                                        <td className="px-2 py-1 text-right">{totals.Quantity.toFixed(4)}</td>
                                        <td className="px-2 py-1 text-right">{totals.Amount.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Remarks"
                    placeholder="Enter verification remarks…"
                    required
                    rows={3}
                    showCharCount
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                            <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                        </div>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                        <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                    </div>
                ) : !hasActions || !enabledActions || enabledActions.length === 0 ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this transfer</p>
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={verifyLoading}
                        isVerified={allRowsChecked}
                        comment={comment}
                        showValidation={true}
                        excludeActions={EXCLUDED_ACTIONS}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'New Stock Received Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={PackagePlus}
                badgeText="New Stock Received"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by ref no, indent, CC…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No New Stock Received transfers pending verification!',
                        itemKey: 'Rid',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'New Stock Received Verification',
                        icon: PackagePlus,
                        headerGradient: 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Transfer Details',
                        icon: PackagePlus,
                        selectedTitle: 'New Stock Received Verification',
                        emptyTitle: 'No Transfer Selected',
                        emptyMessage: 'Select a transfer from the list to view details and take action.',
                        headerGradient: 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyNewStockReceived;
